import { StandardEvents } from '@shopify/events';

/**
 * ALLAN storefront behaviour shared across templates.
 *
 * <allan-cart-count>  Plain number next to "Cart" in the header. Follows the same
 *                     cart events as Horizon's cart icon.
 * <allan-slideshow>   Image slideshow with "01 / 03" counter and Prev / Next.
 *                     data-mode="fade" crossfades stacked slides,
 *                     data-mode="scroll" uses a scroll-snap track (swipeable).
 * Header              Keeps --header-height in sync and closes the mobile menu
 *                     on outside click or Escape.
 * Product gallery     Adds a "01 / 05" counter next to Horizon's gallery dots.
 */

/** @param {number} n */
const pad = (n) => String(n).padStart(2, '0');

class AllanCartCount extends HTMLElement {
  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /** @param {number} count */
  render(count) {
    if (Number.isInteger(count) && count >= 0) this.textContent = String(count);
  }

  /** @param {any} event */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => this.render(cart?.totalQuantity ?? detail?.itemCount ?? 0))
      .catch(() => {});
  };

  /** Pages restored from the back/forward cache may show a stale count. */
  #onPageShow = async (/** @type {PageTransitionEvent} */ event) => {
    if (!event.persisted) return;
    try {
      const response = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (response.ok) this.render((await response.json()).item_count);
    } catch {
      // Keep the server-rendered count.
    }
  };
}

class AllanSlideshow extends HTMLElement {
  index = 0;

  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.counter = this.querySelector('[data-counter]');

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));

    if ((this.dataset.mode === 'scroll' || this.dataset.modeMobile) && this.track) {
      this.track.addEventListener('scroll', this.#onScroll, { passive: true });
    }

    // "Swipe on phones": the mode changes with the screen width.
    if (this.dataset.modeMobile) {
      this.#mobileQuery.addEventListener('change', this.#onModeChange);
    }

    this.#render();
  }

  disconnectedCallback() {
    this.#mobileQuery.removeEventListener('change', this.#onModeChange);
  }

  #mobileQuery = window.matchMedia('(max-width: 749px)');

  #onModeChange = () => {
    if (this.mode === 'scroll') this.go(this.index);
    this.#render();
  };

  get mode() {
    if (this.dataset.modeMobile === 'scroll' && this.#mobileQuery.matches) return 'scroll';
    return this.dataset.mode === 'scroll' ? 'scroll' : 'fade';
  }

  /** @param {number} next */
  go(next) {
    const count = this.slides?.length ?? 0;
    if (count < 2) return;
    this.index = (next + count) % count;

    if (this.mode === 'scroll' && this.track instanceof HTMLElement) {
      const slide = this.slides?.[this.index];
      if (slide instanceof HTMLElement) {
        // Measure on screen: offsetLeft depends on which ancestor is positioned.
        const left = this.track.scrollLeft + slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
        this.track.scrollTo({ left });
      }
    }

    this.#render();
  }

  #onScroll = () => {
    if (this.mode !== 'scroll') return;
    const track = this.track;
    if (!(track instanceof HTMLElement)) return;
    const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
    if (index !== this.index) {
      this.index = index;
      this.#render();
    }
  };

  #render() {
    const count = this.slides?.length ?? 0;
    this.slides?.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === this.index);
      if (this.mode === 'fade') slide.setAttribute('aria-hidden', String(i !== this.index));
      else slide.removeAttribute('aria-hidden');
    });
    if (this.counter) this.counter.textContent = `${pad(this.index + 1)} / ${pad(count)}`;
  }
}

if (!customElements.get('allan-cart-count')) customElements.define('allan-cart-count', AllanCartCount);
if (!customElements.get('allan-slideshow')) customElements.define('allan-slideshow', AllanSlideshow);

/* ---------- Language switch ---------- */

// Return to the same page (including ?variant=, ?page=) after switching language.
// Shopify re-localizes the path, e.g. /products/rest ↔ /sv/products/rest.
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.classList.contains('allan-lang')) return;
  const input = form.querySelector('input[name="return_to"]');
  if (input instanceof HTMLInputElement) input.value = window.location.pathname + window.location.search;
});

/* ---------- Header ---------- */

const header = document.querySelector('.allan-header');

if (header instanceof HTMLElement) {
  const setHeight = () => document.body.style.setProperty('--header-height', `${header.offsetHeight}px`);
  setHeight();
  new ResizeObserver(setHeight).observe(header);

  const menu = header.querySelector('details.allan-menu');
  if (menu instanceof HTMLDetailsElement) {
    document.addEventListener('click', (event) => {
      if (menu.open && event.target instanceof Node && !menu.contains(event.target)) menu.open = false;
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menu.open) {
        menu.open = false;
        menu.querySelector('summary')?.focus();
      }
    });
  }
}

/* ---------- Product gallery counter ---------- */

/**
 * Horizon offers either dots or a counter under the gallery. The ALLAN design
 * shows both, so a counter is appended to every dots control.
 * @param {Element} slideshow
 */
function mountGalleryCounter(slideshow) {
  const controls = Array.from(slideshow.querySelectorAll('slideshow-controls')).filter(
    (el) => el.querySelector('.slideshow-controls__dots') && !el.querySelector('.allan-gallery-counter')
  );
  if (!controls.length) return;

  const total = slideshow.querySelectorAll('slideshow-slide:not([hidden])').length;
  const counters = controls.map((el) => {
    const counter = document.createElement('span');
    counter.className = 'allan-gallery-counter';
    el.append(counter);
    return counter;
  });

  /** @param {number} index */
  const render = (index) => counters.forEach((c) => (c.textContent = `${pad(index + 1)} / ${pad(total)}`));
  const current = Array.from(slideshow.querySelectorAll('slideshow-slide:not([hidden])')).findIndex(
    (s) => s.getAttribute('aria-hidden') === 'false'
  );
  render(Math.max(0, current));

  if (!(slideshow instanceof HTMLElement) || slideshow.dataset.allanCounter) return;
  slideshow.dataset.allanCounter = 'true';
  slideshow.addEventListener('slideshow:select', (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (typeof detail?.index !== 'number') return;
    slideshow.querySelectorAll('.allan-gallery-counter').forEach((c) => {
      c.textContent = `${pad(detail.index + 1)} / ${pad(slideshow.querySelectorAll('slideshow-slide:not([hidden])').length)}`;
    });
  });
}

function mountGalleryCounters() {
  document.querySelectorAll('.product-information media-gallery slideshow-component').forEach(mountGalleryCounter);
}

mountGalleryCounters();
document.addEventListener('shopify:section:load', mountGalleryCounters);

// Variant changes re-render the product section; re-attach counters afterwards.
let counterFrame = 0;
new MutationObserver(() => {
  cancelAnimationFrame(counterFrame);
  counterFrame = requestAnimationFrame(mountGalleryCounters);
}).observe(document.querySelector('main') ?? document.body, { childList: true, subtree: true });
